import { cn } from "../../../utils/cn.js";
import { fieldWrapperVariants, helperTextVariants, iconVariants, labelVariants, selectTriggerVariants, valueTextVariants } from "../shared/form-field-variants.js";
import { ScrollArea, ScrollAreaContent, ScrollAreaScrollbar, ScrollAreaThumb, ScrollAreaViewport } from "../ScrollArea/ScrollArea.js";
import { CaretDownIcon, CheckIcon } from "@phosphor-icons/react";
import * as React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Select as Select$1 } from "@base-ui/react/select";
//#region src/components/Select/Select.tsx
function renderSelectOptionLabel(option) {
	if (!option.icon) return option.label;
	return /* @__PURE__ */ jsxs("span", {
		className: "flex min-w-0 items-center gap-xs",
		children: [/* @__PURE__ */ jsx("span", {
			className: "inline-flex shrink-0 items-center",
			children: option.icon
		}), /* @__PURE__ */ jsx("span", {
			className: "truncate",
			children: option.label
		})]
	});
}
/**
* Curri-styled single-select field built on Base UI primitives.
*
* Mirrors the existing dropdown/select API and visual treatment while using
* Base UI for popup positioning, selection state, and accessibility.
*
* @param props - Select configuration, options, and field presentation props
* @returns The rendered single-select field with label and helper text support
*/
function Select({ "aria-describedby": ariaDescribedBy, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, className, contentClassName, dataFieldId, disableBodyScrollLock = false, disabled, error = false, errorText, helperText, id, itemClassName, label, onOpenChange, onValueChange, options, placeholder = "Select...", size = "base", triggerClassName, triggerIcon, value, ...rootProps }) {
	const reactId = React.useId();
	const triggerId = id || reactId;
	const helperTextId = `${triggerId}-helper-text`;
	const describedBy = [ariaDescribedBy, helperText || errorText && error ? helperTextId : void 0].filter(Boolean).join(" ") || void 0;
	const controlledValueProps = value !== void 0 ? { value } : {};
	const selectedOption = options.find((option) => option.value === value);
	return /* @__PURE__ */ jsxs("div", {
		className: cn(fieldWrapperVariants({ size }), className),
		children: [
			label && /* @__PURE__ */ jsx("label", {
				htmlFor: triggerId,
				className: labelVariants({
					disabled,
					size
				}),
				children: label
			}),
			/* @__PURE__ */ jsxs(Select$1.Root, {
				disabled,
				items: options,
				modal: !disableBodyScrollLock,
				onOpenChange: (isOpen) => onOpenChange?.(isOpen),
				onValueChange: (nextValue) => {
					if (typeof nextValue === "string") onValueChange?.(nextValue);
				},
				...rootProps,
				...controlledValueProps,
				children: [/* @__PURE__ */ jsxs(Select$1.Trigger, {
					"aria-describedby": describedBy,
					"aria-invalid": error || void 0,
					"aria-label": ariaLabel,
					"aria-labelledby": ariaLabelledBy,
					className: cn(selectTriggerVariants({
						error,
						size
					}), "data-[placeholder]:text-text-tertiary", triggerClassName),
					"data-field-id": dataFieldId,
					id: triggerId,
					children: [
						triggerIcon ? /* @__PURE__ */ jsx("span", {
							className: "inline-flex shrink-0 items-center",
							children: triggerIcon
						}) : null,
						/* @__PURE__ */ jsx(Select$1.Value, {
							className: cn(valueTextVariants({ size }), "min-w-0 max-w-full overflow-hidden whitespace-nowrap text-left text-ellipsis data-[placeholder]:text-text-tertiary"),
							placeholder,
							children: selectedOption ? renderSelectOptionLabel(selectedOption) : null
						}),
						/* @__PURE__ */ jsx("span", {
							className: "absolute right-xs inline-flex h-[16px] w-[16px] items-center justify-center rounded-[2px]",
							children: /* @__PURE__ */ jsx(CaretDownIcon, { className: cn(iconVariants({ size })) })
						})
					]
				}), /* @__PURE__ */ jsx(Select$1.Portal, { children: /* @__PURE__ */ jsx(Select$1.Positioner, {
					align: "start",
					alignItemWithTrigger: false,
					className: "isolate z-[100032]",
					sideOffset: 8,
					children: /* @__PURE__ */ jsx(Select$1.Popup, {
						className: cn("font-body z-50 w-[var(--anchor-width)] origin-(--transform-origin) overflow-hidden rounded-[var(--radius-sm)] border border-(length:--border-width-default) border-border-primary bg-elevation-surface-overlay shadow-elevation-overlay [--popup-max-height:300px]", "transition-[transform,scale,opacity] data-[ending-style]:scale-90 data-[ending-style]:opacity-0 data-[starting-style]:scale-90 data-[starting-style]:opacity-0", contentClassName),
						children: /* @__PURE__ */ jsxs(ScrollArea, { children: [/* @__PURE__ */ jsx(ScrollAreaViewport, {
							className: "max-h-[var(--popup-max-height)] [--scroll-fade-color:var(--color-elevation-surface-overlay)]",
							scrollFade: true,
							children: /* @__PURE__ */ jsx(ScrollAreaContent, {
								className: "!min-w-0",
								children: /* @__PURE__ */ jsx(Select$1.List, {
									className: "flex w-full flex-col gap-xxxs p-xxs",
									children: options.map((option) => /* @__PURE__ */ jsxs(Select$1.Item, {
										className: cn("relative flex min-w-0 select-none items-center gap-xs overflow-hidden rounded-xs px-xs py-xs outline-none", "text-body-medium leading-body-medium font-heading-medium text-text-primary", "not-data-[disabled]:cursor-pointer not-data-[disabled]:data-[highlighted]:bg-elevation-surface-overlay-hover not-data-[disabled]:active:bg-elevation-surface-overlay-pressed", "data-[selected]:bg-elevation-surface-overlay-pressed", "data-[disabled]:pointer-events-none data-[disabled]:text-text-disabled", itemClassName),
										disabled: option.disabled,
										label: option.label,
										value: option.value,
										children: [/* @__PURE__ */ jsx(Select$1.ItemText, {
											className: "flex-1",
											children: option.description ? /* @__PURE__ */ jsxs("span", {
												className: "flex min-w-0 flex-col",
												children: [/* @__PURE__ */ jsx("span", {
													className: "truncate",
													children: renderSelectOptionLabel(option)
												}), /* @__PURE__ */ jsx("span", {
													className: "whitespace-normal text-body-medium leading-body-medium font-body-regular text-text-secondary",
													children: option.description
												})]
											}) : renderSelectOptionLabel(option)
										}), /* @__PURE__ */ jsx(Select$1.ItemIndicator, {
											className: "ml-auto flex shrink-0 items-center",
											children: /* @__PURE__ */ jsx(CheckIcon, { className: "size-[16px]" })
										})]
									}, option.value))
								})
							})
						}), /* @__PURE__ */ jsx(ScrollAreaScrollbar, { children: /* @__PURE__ */ jsx(ScrollAreaThumb, {}) })] })
					})
				}) })]
			}),
			(helperText || errorText && error) && /* @__PURE__ */ jsx("p", {
				className: cn(helperTextVariants({
					disabled,
					error,
					size
				})),
				id: helperTextId,
				children: errorText && error ? errorText : helperText
			})
		]
	});
}
//#endregion
export { Select };

//# sourceMappingURL=Select.js.map